import { CaretDownIcon } from '@phosphor-icons/react';
import { Dropdown, Layout, Menu, type MenuProps } from 'antd';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { UserAvatar } from '@/components/common/user-avatar';
import { nav, type NavItem } from '@/config/nav';
import { useLogout } from '@/features/auth';
import { useAuth } from '@/providers/auth-provider';

import styles from './sidebar.module.scss';
import { useSidebarStore } from './use-sidebar-store';

const { Sider } = Layout;

function toMenuItem(
  it: NavItem,
  roles: string[] | undefined,
): NonNullable<MenuProps['items']>[number] {
  return {
    key: it.key,
    label: it.label,
    icon: it.icon,
    ...(it.children
      ? { children: it.children.filter((c) => visible(c, roles)).map((c) => toMenuItem(c, roles)) }
      : {}),
  };
}

function visible(it: NavItem, roles: string[] | undefined): boolean {
  return !it.roles || it.roles.some((r) => roles?.includes(r));
}

function buildItems(items: NavItem[], roles: string[] | undefined): MenuProps['items'] {
  const out: NonNullable<MenuProps['items']> = [];
  let currentSection: string | undefined;
  let bucket: NonNullable<MenuProps['items']> = [];

  const flush = () => {
    if (currentSection && bucket.length) {
      out.push({ type: 'group', label: currentSection, children: bucket });
    } else {
      out.push(...bucket);
    }
    bucket = [];
  };

  for (const it of items) {
    if (!visible(it, roles)) continue;
    if (it.section !== currentSection) {
      flush();
      currentSection = it.section;
    }
    bucket.push(toMenuItem(it, roles));
  }
  flush();
  return out;
}

function flatToPath(items: NavItem[], map: Record<string, string>) {
  for (const it of items) {
    if (it.to) map[it.key] = it.to;
    if (it.children) flatToPath(it.children, map);
  }
}

export function Sidebar() {
  const { collapsed } = useSidebarStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();

  const items = useMemo(() => buildItems(nav, user?.roles), [user?.roles]);

  const keyToPath = useMemo(() => {
    const m: Record<string, string> = {};
    flatToPath(nav, m);
    return m;
  }, []);

  const selectedKey = useMemo(() => {
    const matches = Object.entries(keyToPath)
      .filter(([, path]) => location.pathname === path || location.pathname.startsWith(`${path}/`))
      .sort((a, b) => b[1].length - a[1].length);
    return matches[0]?.[0];
  }, [keyToPath, location.pathname]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={215}
      collapsedWidth={64}
      className={styles.sider}
    >
      <div className={styles.brand}>{collapsed ? 'GA' : 'Greenway Admin'}</div>
      <Menu
        className={styles.menu}
        mode="inline"
        theme="light"
        inlineIndent={32}
        items={items}
        selectedKeys={selectedKey ? [selectedKey] : []}
        onClick={({ key }) => {
          const path = keyToPath[key];
          if (path) navigate(path);
        }}
      />
      {user ? (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'logout',
                label: 'Log out',
                onClick: async () => {
                  await logout.mutateAsync();
                  navigate('/login');
                },
              },
            ],
          }}
        >
          <button type="button" className={styles.userCard}>
            <UserAvatar name={user.name} size="lg" label={user.name} subtitle={user.roles?.[0]} />
            <CaretDownIcon size={14} className={styles.userCaret} />
          </button>
        </Dropdown>
      ) : null}
    </Sider>
  );
}
